import React from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import SegmentsUi from "../../ui/Segments";
import { SegmentsBlock } from "../../lib/storyblok/types/blocks/SegmentsBlock";
import Link from "next/link";
import Button from "../../ui/Button";
import { useLinks } from "../../lib/storyblok/useLinks";
import { useContentStyle, useButtonStyle } from "../../hooks/useStyle";
import { isValidImageData, imageSizes } from "../../lib/utils/imageUtils";
import ImageVaultImage from "../ImageVaultImage";
import { SegmentsProvider } from "../../ui/Segments/components/SegmentsContext";
import OverlayBlock from "./OverlayBlock";
import ShortFeatures from "./ShortFeatures";

const Segments: StoryblokBlockComponent<SegmentsBlock> = ({
  title,
  description,
  buttons,
  contentAlignment,
  innerSpacing,
  outerSpacing,
  style,
  items,
  blocks,
}) => {
  const resolveLink = useLinks();
  const isInverted = useContentStyle(style);
  const { buttonVariant } = useButtonStyle(style);

  const buttonCollection = buttons?.map((button) => (
    <Link
      key={button._uid}
      href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
      passHref
    >
      <Button
        variant={button.buttonType || buttonVariant || "default"}
        href={resolveLink(button.buttonLink)}
      >
        {button.buttonText}
      </Button>
    </Link>
  ));

  return (
    <SegmentsProvider>
      <SegmentsUi
        title={title}
        description={description}
        buttons={buttonCollection}
        style={style}
        contentAlignment={contentAlignment}
        innerSpacing={innerSpacing}
        outerSpacing={outerSpacing}
        inverted={isInverted}
      >
        <SegmentsUi.ItemsWrapper spaceHorizontal={items?.length > 0}>
          {items?.map((item) => (
            <SegmentsUi.Item
              key={item._uid}
              _uid={item._uid}
              label={item.label}
              title={item.title}
              description={item.description}
              image={
                item.image && isValidImageData(item.image) ? (
                  <ImageVaultImage
                    image={item.image}
                    layout="fill"
                    objectFit="cover"
                    sizes={imageSizes("540px", { desktop: "960px" })}
                  />
                ) : null
              }
              style={item.style}
              opacity={item.opacity?.value}
              buttons={item.buttons?.map((button) => (
                <Link
                  key={button._uid}
                  href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
                  passHref
                >
                  <Button
                    variant={button.buttonType || "default"}
                    href={resolveLink(button.buttonLink)}
                  >
                    {button.buttonText}
                  </Button>
                </Link>
              ))}
              list={item.list?.map((listItem) => ({
                icon: listItem.icon,
                text: listItem.text,
                _uid: listItem._uid,
              }))}
            />
          ))}
        </SegmentsUi.ItemsWrapper>
        {blocks?.map((block) => {
          if (block.component === "overlayBlock") {
            return <OverlayBlock key={block._uid} {...block} />;
          } else if (block.component === "shortFeatures") {
            return <ShortFeatures key={block._uid} {...block} />;
          }
          return null;
        })}
      </SegmentsUi>
    </SegmentsProvider>
  );
};

export default Segments;
